import { BaseService } from './BaseService';
import { apiService } from './apiService';
import { ChatSession, ChatMessage } from '../entities';
import { 
  ChatSessionsResponse, 
  ChatMessagesResponse, 
  LatestMessagesResponse,
  CreateSessionRequest,
  SendMessageRequest,
  SendMessageResponse,
  DeleteResponse,
  ChatSessionData,
  ChatMessageData,
  CancelRunsResponse,
  RunsStatusResponse
} from '../types/api';
import { PAGINATION } from '../config/defaults';

export interface NewSessionContent {
  success?: boolean;
  message: string;
  length?: number;
  timestamp?: string;
  // Legacy properties for backward compatibility
  markdown?: string;
  features?: Array<{
    icon: string;
    title: string;
    description: string;
  }>;
  welcomeMessage?: {
    greeting: string;
    prompt: string;
  };
}

export class ChatService extends BaseService {
  constructor() {
    super('ChatService', { debug: false });
  }

  /**
   * Create new chat session
   */
  async createSession(title: string = 'New Chat'): Promise<ChatSession> {
    return this.executeOperation(async () => {
      const response = await apiService.post<ChatSessionData>(
        '/api/chat/session',
        { title } as CreateSessionRequest
      );
      
      return new ChatSession(response);
    }, 'createSession');
  }

  /**
   * Get user's chat sessions with pagination
   */
  async getSessions(
    limit: number = PAGINATION.CHAT_SESSIONS_DEFAULT_LIMIT, 
    continuationToken?: string
  ): Promise<ChatSessionsResponse> {
    return this.executeOperation(async () => {
      let endpoint = `/api/chat/sessions?limit=${limit}`;
      if (continuationToken) {
        endpoint += `&continuationToken=${encodeURIComponent(continuationToken)}`;
      }
      
      return await apiService.get<ChatSessionsResponse>(endpoint);
    }, 'getSessions');
  }

  /**
   * Get specific chat session metadata
   */
  async getSession(sessionId: string): Promise<ChatSession> {
    return this.executeOperation(async () => {
      const response = await apiService.get<ChatSessionData>(`/api/chat/session/${sessionId}`);
      return new ChatSession(response);
    }, 'getSession');
  }

  /**
   * Delete chat session and its messages
   */
  async deleteSession(sessionId: string): Promise<DeleteResponse> {
    return this.executeOperation(async () => {
      return await apiService.delete<DeleteResponse>(`/api/chat/session/${sessionId}`);
    }, 'deleteSession');
  }

  /**
   * Send message to AI chatbot (returns immediately, streaming via SignalR)
   */
  async sendMessage(sessionId: string, message: string): Promise<SendMessageResponse> {
    return this.executeOperation(async () => {
      return await apiService.post<SendMessageResponse>(
        '/api/chat/message',
        { sessionId, message } as SendMessageRequest
      );
    }, 'sendMessage');
  }

  /**
   * Get all messages in a chat session with pagination
   */
  async getSessionMessages(
    sessionId: string,
    limit: number = PAGINATION.MESSAGES_DEFAULT_LIMIT,
    continuationToken?: string
  ): Promise<ChatMessagesResponse> {
    return this.executeOperation(async () => {
      let endpoint = `/api/chat/session/${sessionId}/messages?limit=${limit}`;
      if (continuationToken) {
        endpoint += `&continuationToken=${encodeURIComponent(continuationToken)}`;
      }
      
      return await apiService.get<ChatMessagesResponse>(endpoint);
    }, 'getSessionMessages');
  }

  /**
   * Get latest N messages for context
   */
  async getLatestMessages(
    sessionId: string,
    limit: number = PAGINATION.LATEST_MESSAGES_LIMIT
  ): Promise<LatestMessagesResponse> {
    return this.executeOperation(async () => {
      const endpoint = `/api/chat/session/${sessionId}/messages/latest?limit=${limit}`;
      return await apiService.get<LatestMessagesResponse>(endpoint);
    }, 'getLatestMessages');
  }

  /**
   * Get new session welcome message/content
   */
  async getNewSessionMessage(): Promise<NewSessionContent> {
    return this.executeOperation(async () => {
      const response = await apiService.get<NewSessionContent>('/api/chat/new-session-message');
      return response;
    }, 'getNewSessionMessage');
  }

  /**
   * Delete specific message (user messages only)
   */
  async deleteMessage(messageId: string): Promise<DeleteResponse> {
    return this.executeOperation(async () => {
      return await apiService.delete<DeleteResponse>(`/api/chat/message/${messageId}`);
    }, 'deleteMessage');
  }

  /**
   * Cancel active AI runs for a session
   * Used when user wants to interrupt an ongoing AI response
   */
  async cancelRuns(sessionId: string): Promise<CancelRunsResponse> {
    return this.executeOperation(async () => {
      return await apiService.post<CancelRunsResponse>(
        `/api/chat/session/${sessionId}/cancel-runs`
      );
    }, 'cancelRuns');
  }

  /**
   * Get active runs status for a session
   * Used to check if there are active AI runs before sending a new message
   */
  async getActiveRuns(sessionId: string): Promise<RunsStatusResponse> {
    return this.executeOperation(async () => {
      return await apiService.get<RunsStatusResponse>(
        `/api/chat/session/${sessionId}/runs/active`
      );
    }, 'getActiveRuns');
  }

  /**
   * Convert API chat session data to ChatSession entities
   */
  mapSessionDataToEntities(sessions: ChatSessionData[]): ChatSession[] {
    return sessions.map(sessionData => new ChatSession(sessionData));
  }

  /**
   * Convert API chat message data to ChatMessage entities
   */
  mapMessageDataToEntities(messages: ChatMessageData[]): ChatMessage[] {
    return messages.map(messageData => new ChatMessage(messageData));
  }

  protected onCircuitBreakerChange(eventType: string, data: any): void {
    this.logDebug(`Circuit breaker event: ${eventType}`, data);
  }

  protected onRetry(error: Error, attempt: number, delay: number, operationName: string): void {
    this.logDebug(`Retrying ${operationName}`, { 
      attempt, 
      delay, 
      error: error.message 
    });
  }
}

export const chatService = new ChatService();
